import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, ShoppingBag, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trackPublish } from "@/lib/fbPixel";
import api, { resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import PurchaseModal from "@/components/PurchaseModal";
import CopyrightNotice from "@/components/CopyrightNotice";

const TYPE_LABEL = { album: "Album", ep: "EP", maxi: "Maxi" };

// Lowest price a song may have: strictly more than `shared` split equally between `count` songs,
// in cents. Same rule as the server (routers/releases.py).
const minSongPrice = (shared, count) => (Math.floor((shared * 100) / count + 1e-9) + 1) / 100;
const isBonus = (t) => t.is_free || Number(t.price) <= 0;
const isExisting = (t) => t.release_origin === "existing";

/** Project page: one cover, the ordered list of songs, each playable and
 *  purchasable on its own (like an album page on Spotify, sold per song). */
export default function ReleasePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { current, playing, playTrack, togglePlay } = usePlayer();
  const [rel, setRel] = useState(null);
  const [missing, setMissing] = useState(false);
  const [buyTrack, setBuyTrack] = useState(null);
  const [buyRelease, setBuyRelease] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/releases/${id}`); setRel(data); setMissing(false); }
    catch { setMissing(true); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, user?.id]);

  if (missing) return <p className="text-center text-zinc-500 py-16" data-testid="release-missing">Ce projet n'existe pas ou n'est plus disponible.</p>;
  if (!rel) return <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-500 inline" /></div>;

  const tracks = rel.tracks || [];
  const ownedCount = tracks.filter((t) => t.owned).length;
  const playAll = () => { if (tracks.length) playTrack(tracks[0], tracks); };
  const savings = rel.price && rel.songs_total > rel.price ? Math.round((rel.songs_total - rel.price) * 100) / 100 : 0;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6" data-testid="release-page">
      <Link to={`/profile/${rel.artist_id}`} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-5">
        <ArrowLeft className="w-4 h-4" /> {rel.artist_name}
      </Link>

      <header className="flex flex-col sm:flex-row gap-5 sm:gap-8 items-start">
        <img src={resolveImg(rel.cover)} alt={rel.title} className="w-48 h-48 sm:w-60 sm:h-60 rounded-2xl object-cover shadow-2xl" />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00F2FE] font-bold">{TYPE_LABEL[rel.release_type] || rel.release_type}</p>
          <h1 className="text-3xl sm:text-4xl font-black font-display text-white mt-1" data-testid="release-title">{rel.title}</h1>
          <p className="text-zinc-300 mt-1"><Link to={`/profile/${rel.artist_id}`} className="hover:underline">{rel.artist_name}</Link> · {tracks.length} titre{tracks.length > 1 ? "s" : ""}{rel.genre ? ` · ${rel.genre}` : ""}</p>
          {rel.description && <p className="text-sm text-zinc-400 mt-3 max-w-xl whitespace-pre-wrap">{rel.description}</p>}
          {user && ownedCount > 0 && (
            <p className="mt-3 text-sm text-emerald-300" data-testid="release-owned">
              {rel.complete ? "Tu possèdes tout le projet" : `Tu possèdes ${rel.owned_paid_count} chanson${rel.owned_paid_count > 1 ? "s" : ""} payante${rel.owned_paid_count > 1 ? "s" : ""} sur ${rel.paid_count}`}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button onClick={playAll} disabled={!tracks.length} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="release-play-all">
              <Play className="w-4 h-4" /> Écouter
            </button>
            {rel.can_buy && (
              <button
                onClick={() => (user ? setBuyRelease({ id: rel.id, kind: "release", title: rel.title, cover: rel.cover, artist_name: rel.artist_name, price: rel.price, currency: rel.currency || "USD" }) : toast.error("Connecte-toi pour acheter."))}
                className="btn-secondary inline-flex items-center gap-2"
                data-testid="release-buy"
              >
                <ShoppingBag className="w-4 h-4" /> Acheter le projet · ${rel.price.toFixed(2)}
              </button>
            )}
          </div>
          {rel.can_buy && savings > 0 && (
            <p className="mt-2 text-xs text-emerald-300" data-testid="release-savings">
              Tout le projet pour ${rel.price.toFixed(2)} au lieu de ${rel.songs_total.toFixed(2)} chanson par chanson : tu économises ${savings.toFixed(2)}.
            </p>
          )}
          {rel.is_owner && <OwnerPanel rel={rel} tracks={tracks} setRel={setRel} reload={load} />}
        </div>
      </header>

      <ol className="mt-8 rounded-2xl border border-white/10 overflow-hidden divide-y divide-white/5" data-testid="release-tracks">
        {tracks.length === 0 && <li className="p-6 text-center text-zinc-500 text-sm">Aucune chanson pour le moment.</li>}
        {tracks.map((t, i) => {
          const isCurrent = current?.id === t.id;
          return (
            <li key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03]" data-testid={`release-track-${t.id}`}>
              <span className="w-6 text-center text-sm text-zinc-500 tabular-nums">{i + 1}</span>
              <button
                onClick={() => (isCurrent ? togglePlay() : playTrack(t, tracks))}
                className="w-9 h-9 rounded-full bg-white text-black grid place-items-center shrink-0 active:scale-95"
                aria-label="play"
                data-testid={`release-track-play-${t.id}`}
              >
                {isCurrent && playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-white truncate">{t.title}</p>
                {t.feat?.length ? <p className="text-xs text-zinc-500 truncate">feat. {t.feat.join(", ")}</p> : null}
                {isExisting(t) && <p className="text-[10px] text-zinc-500">Déjà publiée avant le projet</p>}
              </div>
              <span className="text-sm font-black text-white tabular-nums">{t.is_free ? (rel.price ? "BONUS" : "FREE") : `$${t.price.toFixed(2)}`}</span>
              {t.owned ? (
                <span className="inline-flex items-center gap-1 chip bg-emerald-500/15 text-emerald-300"><Check className="w-3 h-3" /> Possédé</span>
              ) : t.is_free ? (
                <span className="chip bg-[#00F2FE]/15 text-[#00F2FE]">{rel.price ? "BONUS" : "FREE"}</span>
              ) : (
                <button onClick={() => setBuyTrack(t)} className="btn-primary py-1.5 px-3 text-xs inline-flex items-center gap-1" data-testid={`release-track-buy-${t.id}`}>
                  <ShoppingBag className="w-3.5 h-3.5" /> Acheter
                </button>
              )}
            </li>
          );
        })}
      </ol>

      {buyTrack && <PurchaseModal track={buyTrack} onClose={() => setBuyTrack(null)} onSuccess={() => load()} />}
      {buyRelease && <PurchaseModal track={buyRelease} onClose={() => setBuyRelease(null)} onSuccess={() => load()} />}
      <div className="mt-10 pt-6 border-t border-white/5"><CopyrightNotice /></div>
    </div>
  );
}

/** The artist's own controls: while a draft — add songs already published, set the price
 *  of the project and of its new songs, publish; once published — change prices (checked). */
function OwnerPanel({ rel, tracks, setRel, reload }) {
  const [price, setPrice] = useState("");
  const [songPrices, setSongPrices] = useState({}); // {song id: "2.50"} typed for NEW songs
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  const [pick, setPick] = useState("");
  const [priceEdit, setPriceEdit] = useState({}); // published: {song id: "2.50"}

  useEffect(() => {
    if (rel.published) return;
    api.get("/tracks", { params: { artist_id: rel.artist_id, limit: 500 } })
      .then(({ data }) => setMine((data || []).filter((t) => !t.release_id && !t.draft)))
      .catch(() => {});
  }, [rel.published, rel.artist_id, tracks.length]);

  const fail = (e) => toast.error(e.response?.data?.detail || "Erreur");
  const priceOf = (t) => {
    const typed = songPrices[t.id];
    return typed !== undefined && !isExisting(t) ? parseFloat(typed) || 0 : Number(t.price);
  };

  const attach = async () => {
    if (!pick) return;
    setBusy(true);
    try { const { data } = await api.post(`/releases/${rel.id}/songs`, { track_id: pick }); setRel(data); setPick(""); toast.success("Chanson ajoutée au projet."); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };
  const detach = async (tid) => {
    setBusy(true);
    try { const { data } = await api.delete(`/releases/${rel.id}/songs/${tid}`); setRel(data); }
    catch (e) { fail(e); } finally { setBusy(false); }
  };

  // The same rules as the server, to guide the artist before they press the button.
  const P = parseFloat(price);
  const paid = tracks.filter((t) => priceOf(t) > 0);
  const oldPaid = paid.filter(isExisting);
  const newPaid = paid.filter((t) => !isExisting(t));
  const oldTotal = oldPaid.reduce((s, t) => s + priceOf(t), 0);
  const remaining = P - oldTotal;
  const floorPrice = newPaid.length ? (remaining > 0 ? minSongPrice(remaining, newPaid.length) : 0.01) : null;
  const total = paid.reduce((s, t) => s + priceOf(t), 0);
  const bonuses = tracks.filter((t) => priceOf(t) <= 0);

  const publish = async (e) => {
    e.preventDefault();
    if (!(P > 0)) { toast.error("Indique le prix du projet."); return; }
    const song_prices = {};
    for (const t of tracks) {
      if (isExisting(t)) continue;
      const typed = songPrices[t.id];
      if (typed !== undefined && parseFloat(typed) !== Number(t.price)) song_prices[t.id] = parseFloat(typed);
    }
    if (!window.confirm(`Publier « ${rel.title} » à $${P.toFixed(2)} ? Après publication, tu ne pourras plus ajouter ni retirer de chanson (pour une réédition, crée un nouveau projet).`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/releases/${rel.id}/publish`, { price: P, song_prices }); setRel(data); trackPublish("release"); toast.success("Projet publié : il est en vente."); }
    catch (e2) { fail(e2); } finally { setBusy(false); }
  };

  const changeProjectPrice = async (e) => {
    e.preventDefault();
    if (!(P > 0)) return;
    setBusy(true);
    try { const { data } = await api.patch(`/releases/${rel.id}`, { price: P }); setRel(data); setPrice(""); toast.success("Prix du projet mis à jour."); }
    catch (e2) { fail(e2); } finally { setBusy(false); }
  };
  const changeSongPrice = async (t) => {
    const v = parseFloat(priceEdit[t.id]);
    if (!(v > 0)) return;
    setBusy(true);
    try { await api.patch(`/tracks/${t.id}/price`, { price: v }); setPriceEdit({ ...priceEdit, [t.id]: undefined }); await reload(); toast.success("Prix de la chanson mis à jour."); }
    catch (e2) { fail(e2); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 max-w-md space-y-4" data-testid="release-owner-panel">
      {rel.published ? (
        <>
          <form onSubmit={changeProjectPrice} className="space-y-2">
            <p className="text-sm text-emerald-300 font-bold">Publié · en vente à ${Number(rel.price).toFixed(2)}</p>
            <div className="flex gap-2">
              <input type="number" step="0.01" min="0.25" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Nouveau prix du projet (0,25 $ min.)" className="input-base flex-1" data-testid="release-new-price" />
              <button disabled={busy || !price} className="btn-secondary disabled:opacity-50">Modifier</button>
            </div>
            <p className="text-[11px] text-zinc-500">Le projet doit toujours coûter moins que ses chansons une par une. La liste des chansons est figée : pour une réédition avec de nouvelles chansons, crée un nouveau projet.</p>
          </form>
          <div className="space-y-2" data-testid="release-song-price-edit">
            <p className="text-xs text-zinc-400 font-bold">Prix des chansons (les achats déjà faits ne changent pas)</p>
            {tracks.filter((t) => !isBonus(t)).map((t) => (
              <div key={t.id} className="flex items-center gap-2">
                <span className="flex-1 truncate text-sm text-white">{t.title} <span className="text-zinc-500">· ${t.price.toFixed(2)}</span></span>
                <input type="number" step="0.01" min="0.01" value={priceEdit[t.id] ?? ""} onChange={(e) => setPriceEdit({ ...priceEdit, [t.id]: e.target.value })} placeholder="Nouveau" className="input-base w-24" data-testid={`song-new-price-${t.id}`} />
                <button type="button" disabled={busy || !priceEdit[t.id]} onClick={() => changeSongPrice(t)} className="btn-secondary text-xs disabled:opacity-50">OK</button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-amber-300 font-bold">Brouillon · visible seulement par toi</p>
          <div className="space-y-2" data-testid="release-attach">
            <p className="text-xs text-zinc-400">Tu peux ajouter une chanson déjà publiée : elle garde son prix et sa pochette, et ceux qui l'ont achetée la gardent.</p>
            <div className="flex gap-2">
              <select value={pick} onChange={(e) => setPick(e.target.value)} className="input-base flex-1" data-testid="release-attach-select">
                <option value="">Choisir une chanson déjà publiée…</option>
                {mine.map((t) => <option key={t.id} value={t.id}>{t.title} ({t.is_free ? "gratuite" : `$${t.price.toFixed(2)}`})</option>)}
              </select>
              <button type="button" disabled={busy || !pick} onClick={attach} className="btn-secondary disabled:opacity-50" data-testid="release-attach-btn">Ajouter</button>
            </div>
            {tracks.filter(isExisting).map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-xs text-zinc-300">
                <span className="flex-1 truncate">{t.title} · déjà publiée · prix fixe {t.is_free ? "(bonus gratuit)" : `$${t.price.toFixed(2)}`}</span>
                <button type="button" onClick={() => detach(t.id)} className="text-red-300 underline" data-testid={`release-detach-${t.id}`}>Retirer</button>
              </div>
            ))}
          </div>

          <form onSubmit={publish} className="space-y-3">
            <p className="text-xs text-zinc-400">
              {tracks.length} chanson{tracks.length > 1 ? "s" : ""} dont {paid.length} payante{paid.length > 1 ? "s" : ""}{bonuses.length > 0 ? ` et ${bonuses.length} bonus gratuit${bonuses.length > 1 ? "s" : ""} (hors calcul)` : ""}. Ajoute toutes tes chansons avant de publier (au moins 2 payantes).
            </p>
            <input type="number" step="0.01" min="0.25" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Prix du projet ($, 0,25 min.)" className="input-base w-full" data-testid="release-price" />
            {P > 0 && paid.length >= 2 && (
              <div className="space-y-2" data-testid="release-song-prices">
                {newPaid.length > 0 && (
                  <p className="text-xs text-zinc-300">
                    {oldPaid.length > 0
                      ? <>Prix du projet − chansons déjà publiées ({oldTotal.toFixed(2)} $) = {Math.max(remaining, 0).toFixed(2)} $ à partager entre les {newPaid.length} nouvelle{newPaid.length > 1 ? "s" : ""} chanson{newPaid.length > 1 ? "s" : ""} payante{newPaid.length > 1 ? "s" : ""}. </>
                      : <>Prix du projet ÷ {newPaid.length} chansons payantes = {(P / newPaid.length).toFixed(2)} $. </>}
                    <strong>Chaque nouvelle chanson payante doit coûter plus que sa part</strong> (au moins {floorPrice.toFixed(2)} $).
                  </p>
                )}
                {paid.map((t) => {
                  const old = isExisting(t);
                  const typed = parseFloat(songPrices[t.id] ?? t.price);
                  const low = !old && floorPrice !== null && !(typed >= floorPrice);
                  return (
                    <div key={t.id} className="flex items-center gap-2">
                      <span className="flex-1 truncate text-sm text-white">{t.title}{old ? <span className="text-zinc-500"> · déjà publiée</span> : null}</span>
                      {old ? (
                        <span className="w-24 text-right text-sm text-zinc-300 tabular-nums">${t.price.toFixed(2)}</span>
                      ) : (
                        <input type="number" step="0.01" min="0.01" value={songPrices[t.id] ?? String(t.price)} onChange={(e) => setSongPrices({ ...songPrices, [t.id]: e.target.value })} className={`input-base w-24 ${low ? "border-red-500/60" : ""}`} data-testid={`release-song-price-${t.id}`} />
                      )}
                      <span className="text-xs text-zinc-500 w-4">$</span>
                    </div>
                  );
                })}
                {floorPrice !== null && newPaid.length > 0 && (
                  <button type="button" onClick={() => setSongPrices(Object.fromEntries(newPaid.map((t) => [t.id, Math.max(priceOf(t), floorPrice).toFixed(2)])))} className="text-xs text-[#00F2FE] underline" data-testid="release-auto-prices">
                    Relever automatiquement les nouvelles chansons trop basses
                  </button>
                )}
                <p className={`text-xs ${P < total ? "text-emerald-300" : "text-red-300"}`}>
                  Les chansons une par une : {total.toFixed(2)} $. {P < total ? `Le projet à ${P.toFixed(2)} $ est moins cher : OK.` : "Le projet doit coûter moins que ça."}
                </p>
              </div>
            )}
            <button disabled={busy || paid.length < 2} className="btn-primary w-full disabled:opacity-50" data-testid="release-publish">Publier le projet</button>
          </form>
        </>
      )}
    </div>
  );
}
